<template>
  <div class="permission-explorer" data-testid="permission-explorer">
    <div class="explorer-content">
      <v-progress-linear
        v-if="loading"
        indeterminate
        aria-label="Loading role permissions"
      />
      <div class="explorer-context">
        <div class="context-role">
          <div>
            <span class="context-label">Permissions for</span>
            <h3>{{ role.roleName }}</h3>
          </div>
          <span class="preview-label"
            ><v-icon x-small>{{
              managed ? "mdi-shield-check-outline" : "mdi-flask-outline"
            }}</v-icon>
            {{ managed ? "Live permissions" : "Layout preview" }}</span
          >
        </div>
        <div class="explorer-search">
          <v-text-field
            v-model="search"
            outlined
            dense
            clearable
            hide-details
            label="Find a menu or action"
            placeholder="Try trainee, certificate or approve"
            prepend-inner-icon="mdi-magnify"
          />
          <div class="catalog-meta">
            <span>{{
              search
                ? matchingPageCount + " matching pages"
                : pages.length + " pages in " + sections.length + " sections"
            }}</span
            ><button v-if="search" type="button" @click="search = ''">
              Clear search</button
            ><span v-else>{{ allowedPageCount }} pages enabled</span>
          </div>
        </div>
      </div>
      <p class="context-help">
        Choose a page, then select what this role can do.
      </p>
      <v-alert v-if="storageNotice" dense text type="info" class="mx-4">{{
        storageNotice
      }}</v-alert>
      <v-alert v-if="error" dense text type="error" class="mx-4">{{
        error
      }}</v-alert>
      <nav class="menu-categories" aria-label="Menu categories">
        <button
          v-for="section in sections"
          :key="section.id"
          type="button"
          :class="{ 'category-selected': activeCategoryId === section.id }"
          :aria-pressed="activeCategoryId === section.id"
          @click="chooseCategory(section.id)"
        >
          <v-icon small>{{ section.icon }}</v-icon
          ><span>{{ section.label }}</span>
        </button>
      </nav>
      <div class="menu-picker">
        <div class="picker-heading">
          <strong>{{
            search ? "Search results" : selectedCategory.label + " pages"
          }}</strong
          ><span
            >{{ pageChoices.length }}
            {{ pageChoices.length === 1 ? "page" : "pages" }}</span
          >
        </div>
        <div class="page-choices" aria-label="Choose a page">
          <button
            v-for="option in pageChoices"
            :key="option.page.id"
            type="button"
            class="page-choice"
            :class="{
              'page-choice--selected':
                selectedPage && selectedPage.id === option.page.id,
            }"
            :aria-pressed="!!selectedPage && selectedPage.id === option.page.id"
            @click="choosePage(option.page.id)"
          >
            <v-icon small>{{ option.page.icon }}</v-icon
            ><span
              ><strong>{{ option.page.label }}</strong
              ><small v-if="option.folder || search">{{
                option.folder || option.sectionLabel
              }}</small></span
            ><v-icon
              v-if="draft[option.page.id].view"
              x-small
              class="page-enabled-icon"
              >mdi-check-circle</v-icon
            >
          </button>
        </div>
      </div>
      <div v-if="selectedPage" class="focused-permissions" ref="focusedEditor">
        <div class="focused-toolbar">
          <span
            >{{ selectedPageOption.sectionLabel
            }}<template v-if="selectedPageOption.folder">
              / {{ selectedPageOption.folder }}</template
            >
            / {{ selectedPage.label }}</span
          ><button
            type="button"
            :aria-pressed="showExplanations"
            @click="showExplanations = !showExplanations"
          >
            <v-icon x-small>mdi-information-outline</v-icon
            >{{ showExplanations ? "Hide explanations" : "Show explanations" }}
          </button>
        </div>
        <div class="permission-tree" aria-label="Selected page permissions">
          <permission-page-card
            :key="selectedPage.id + '-' + renderRevision"
            :page="selectedPage"
            :state="draft[selectedPage.id]"
            expanded
            focused
            :show-explanations="showExplanations"
            :editable="canEdit"
            :managed="managed"
            :role-name="role.roleName"
            :search="selectedActionSearch"
            @view-change="requestView(selectedPage, $event)"
            @action-change="setAction(selectedPage, $event)"
            @scope-change="setScope(selectedPage, $event)"
          />
        </div>
      </div>
      <div v-else class="no-results">
        <v-icon size="30">mdi-magnify</v-icon
        ><strong>No menus or actions found</strong>
        <p>Try a page name or a task such as “certificate”.</p>
        <v-btn text small color="primary" @click="search = ''"
          >Clear search</v-btn
        >
      </div>
    </div>
    <div class="preview-savebar">
      <div>
        <strong>{{
          dirty
            ? changeCount +
              " unsaved " +
              (changeCount === 1 ? "change" : "changes")
            : savedAt
              ? managed
                ? "Permissions saved"
                : "Preview saved on this browser"
              : "No unsaved changes"
        }}</strong>
        <p>
          {{
            editable
              ? managed
                ? "Changes apply to everyone with this role"
                : "Preview selections only · no live access changes"
              : "View only · your account cannot edit permissions"
          }}
        </p>
      </div>
      <div v-if="editable" class="savebar-actions">
        <v-btn text small :disabled="!dirty" @click="discardDraft"
          >Discard</v-btn
        ><v-btn
          color="primary"
          depressed
          small
          :disabled="!dirty"
          @click="reviewDialog = true"
          >Review &amp; save</v-btn
        >
      </div>
    </div>
    <v-dialog v-model="reviewDialog" persistent max-width="630" scrollable
      ><v-card class="preview-dialog"
        ><v-card-title
          ><div>
            <h3>
              {{
                managed
                  ? "Review role permissions"
                  : "Review permission preview"
              }}
            </h3>
            <p>
              {{ role.roleName }} · {{ changeCount }}
              {{ changeCount === 1 ? "change" : "changes" }}
            </p>
          </div>
          <v-spacer /><v-btn
            icon
            aria-label="Close permission review"
            @click="reviewDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        ><v-divider /><v-card-text class="review-body"
          ><div class="review-notice">
            <v-icon small>mdi-flask-outline</v-icon>
            <p>
              {{
                managed
                  ? "Saving changes access for everyone with this role. Record ownership and workflow restrictions still apply."
                  : "This saves a preview on this browser. It does not change anyone’s access."
              }}
            </p>
          </div>
          <v-alert v-if="error" dense text type="error">{{ error }}</v-alert>
          <section v-for="page in changes" :key="page.id" class="review-page">
            <h4>{{ page.label }}</h4>
            <div
              v-for="change in page.changes"
              :key="change.id"
              class="review-change"
            >
              <v-icon
                small
                :color="
                  change.value
                    ? 'primary'
                    : change.enabled
                      ? 'success'
                      : 'error'
                "
                >{{
                  change.value
                    ? "mdi-filter-outline"
                    : change.enabled
                      ? "mdi-plus-circle-outline"
                      : "mdi-minus-circle-outline"
                }}</v-icon
              ><span>{{ change.label }}</span
              ><strong
                :class="
                  change.value
                    ? ''
                    : change.enabled
                      ? 'change-added'
                      : 'change-removed'
                "
                >{{
                  change.value || (change.enabled ? "Allow" : "Remove")
                }}</strong
              >
            </div>
          </section></v-card-text
        ><v-divider /><v-card-actions class="review-actions"
          ><v-btn text @click="reviewDialog = false">Back to editing</v-btn
          ><v-spacer /><v-btn
            depressed
            color="primary"
            :disabled="!canEdit || !dirty"
            :loading="saving"
            @click="savePreview"
            >{{ managed ? "Save permissions" : "Save preview" }}</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
    <v-dialog v-model="viewDialog" persistent max-width="460"
      ><v-card class="preview-dialog"
        ><v-card-title>Turn off page access?</v-card-title
        ><v-card-text v-if="pendingView"
          ><strong>{{ pendingView.label }}</strong>
          <p>
            Its {{ enabledActionCount(pendingView) }} enabled actions will also
            be turned off{{ managed ? " when you save." : " in this preview." }}
          </p></v-card-text
        ><v-card-actions class="review-actions"
          ><v-btn
            text
            @click="
              viewDialog = false;
              pendingView = null;
            "
            >Keep access</v-btn
          ><v-spacer /><v-btn text color="error" @click="confirmViewOff"
            >Turn off access</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
  </div>
</template>
<script>
import ApiService from "@/common/api.service";
import { refreshAccess } from "@/utils/managedAccess";
import PermissionPageCard from "./PermissionPageCard.vue";
import {
  permissionSections,
  permissionPages,
  pageActions,
  makeRolePreview,
  normalizeSavedPreview,
  previewChanges,
  previewSchemaVersion,
  previewStorageKey,
  roleRestriction,
} from "./permission-catalog";
const clone = (value) => JSON.parse(JSON.stringify(value));
const flatPages = (node) =>
  node.type === "page" ? [node] : node.children.flatMap(flatPages);
export default {
  components: { PermissionPageCard },
  props: {
    role: { type: Object, required: true },
    editable: Boolean,
    managed: Boolean,
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadFailed: false,
      revision: null,
      sections: permissionSections,
      pages: permissionPages,
      draft: {},
      original: {},
      search: "",
      selectedCategoryId: "management",
      selectedPageId: "trainee",
      showExplanations: false,
      reviewDialog: false,
      viewDialog: false,
      pendingView: null,
      savedAt: null,
      storageNotice: "",
      error: "",
      renderRevision: 0,
    };
  },
  computed: {
    canEdit() {
      return this.editable && !this.loading && !this.saving && !this.loadFailed;
    },
    changes() {
      return previewChanges(this.original, this.draft);
    },
    changeCount() {
      return this.changes.reduce(
        (count, page) => count + page.changes.length,
        0,
      );
    },
    dirty() {
      return this.changeCount > 0;
    },
    allowedPageCount() {
      return this.pages.filter((page) => this.draft[page.id]?.view).length;
    },
    visibleSections() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return this.sections;
      const filter = (node, inherited = false) => {
        const matched = inherited || node.label.toLowerCase().includes(term);
        if (node.type === "page") {
          const words = [
            node.description,
            ...(node.searchAliases || []),
            ...node.taskGroups.flatMap((group) => [
              group.label,
              group.description,
              ...group.actions.flatMap((action) => [
                action.label,
                action.description,
              ]),
            ]),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return matched || words.includes(term) ? node : null;
        }
        const children = node.children
          .map((child) => filter(child, matched))
          .filter(Boolean);
        return children.length ? { ...node, children } : null;
      };
      return this.sections.map((section) => filter(section)).filter(Boolean);
    },
    matchingPageCount() {
      return this.visibleSections.reduce(
        (count, section) => count + flatPages(section).length,
        0,
      );
    },
    pageOptions() {
      return this.sections.flatMap((section) =>
        section.children.flatMap((node) => {
          const entries = node.type === "page" ? [node] : node.children;
          return entries.map((page) => ({
            page,
            sectionId: section.id,
            sectionLabel: section.label,
            folder: node.type === "page" ? null : node.label,
          }));
        }),
      );
    },
    selectedCategory() {
      return (
        this.sections.find(
          (section) => section.id === this.selectedCategoryId,
        ) || this.sections[0]
      );
    },
    pageChoices() {
      if (!this.search)
        return this.pageOptions.filter(
          (option) => option.sectionId === this.selectedCategoryId,
        );
      const matches = new Set(
        this.visibleSections
          .flatMap((section) => flatPages(section))
          .map((page) => page.id),
      );
      return this.pageOptions.filter((option) => matches.has(option.page.id));
    },
    selectedPageOption() {
      return (
        this.pageChoices.find(
          (option) => option.page.id === this.selectedPageId,
        ) ||
        this.pageChoices[0] ||
        null
      );
    },
    selectedPage() {
      return this.selectedPageOption?.page || null;
    },
    activeCategoryId() {
      return this.search
        ? this.selectedPageOption?.sectionId
        : this.selectedCategoryId;
    },
    selectedActionSearch() {
      if (!this.search || !this.selectedPageOption) return "";
      const term = this.search.trim().toLowerCase();
      return [
        this.selectedPage.label,
        this.selectedPageOption.folder,
        this.selectedPageOption.sectionLabel,
      ].some((label) => label?.toLowerCase().includes(term))
        ? ""
        : this.search;
    },
  },
  watch: {
    dirty: {
      immediate: true,
      handler(value) {
        this.$emit("dirty-change", value);
      },
    },
  },
  created() {
    this.loadPreview();
    if (this.managed) this.loadPolicy();
  },
  methods: {
    async loadPolicy() {
      this.loading = true;
      this.loadFailed = false;
      try {
        const { data } = await ApiService.get(`/access/roles/${this.role.id}`);
        if (data.schemaVersion !== previewSchemaVersion)
          throw new Error(
            "Reload the application to use the current permission catalog.",
          );
        this.draft = clone(data.pages);
        this.original = clone(data.pages);
        this.revision = data.revision;
      } catch (error) {
        this.loadFailed = true;
        this.error =
          error.response?.data?.message ||
          error.message ||
          "Unable to load permissions. Refresh this page to try again.";
      } finally {
        this.loading = false;
      }
    },
    loadPreview() {
      const baseline = makeRolePreview(this.role);
      let state = baseline;
      try {
        const raw = this.managed
          ? null
          : localStorage.getItem(previewStorageKey(this.role.id));
        if (raw) {
          const saved = JSON.parse(raw);
          if (
            saved.roleId === this.role.id &&
            saved.schemaVersion === previewSchemaVersion
          ) {
            state = normalizeSavedPreview(saved, baseline, this.role.roleName);
            this.savedAt = saved.savedAt;
          }
        }
      } catch (error) {
        this.storageNotice =
          "The saved preview could not be read. Showing the current menu settings instead.";
      }
      this.draft = clone(state);
      this.original = clone(state);
    },
    chooseCategory(id) {
      this.search = "";
      this.selectedCategoryId = id;
      if (
        !this.pageOptions.some(
          (option) =>
            option.sectionId === id && option.page.id === this.selectedPageId,
        )
      )
        this.selectedPageId = this.pageOptions.find(
          (option) => option.sectionId === id,
        )?.page.id;
    },
    choosePage(id) {
      this.selectedPageId = id;
      this.selectedCategoryId =
        this.pageOptions.find((option) => option.page.id === id)?.sectionId ||
        this.selectedCategoryId;
    },
    enabledActionCount(page) {
      return pageActions(page).filter(
        (action) => this.draft[page.id].actions[action.id],
      ).length;
    },
    requestView(page, value) {
      if (!this.canEdit) return;
      if (!value && this.enabledActionCount(page)) {
        this.pendingView = page;
        this.viewDialog = true;
        this.renderRevision++;
        return;
      }
      this.$set(this.draft[page.id], "view", !!value);
      if (!value) this.clearPage(page);
    },
    clearPage(page) {
      this.$set(this.draft[page.id], "view", false);
      pageActions(page).forEach((action) =>
        this.$set(this.draft[page.id].actions, action.id, false),
      );
      if (page.recordScope) this.$set(this.draft[page.id], "scope", "assigned");
    },
    confirmViewOff() {
      if (!this.canEdit || !this.pendingView) return;
      this.clearPage(this.pendingView);
      this.pendingView = null;
      this.viewDialog = false;
    },
    setAction(page, { actionId, value }) {
      const action = pageActions(page).find((item) => item.id === actionId);
      if (
        !this.canEdit ||
        !action ||
        (!this.managed && roleRestriction(action, this.role.roleName))
      )
        return;
      this.$set(this.draft[page.id].actions, actionId, !!value);
      if (value) this.$set(this.draft[page.id], "view", true);
    },
    setScope(page, value) {
      if (
        this.canEdit &&
        this.draft[page.id].view &&
        page.recordScope.options.some((option) => option.id === value)
      )
        this.$set(this.draft[page.id], "scope", value);
    },
    discardDraft() {
      this.draft = clone(this.original);
      this.error = "";
      this.renderRevision++;
    },
    async savePreview() {
      if (!this.canEdit || !this.dirty) return;
      this.error = "";
      if (this.managed) {
        this.saving = true;
        try {
          const { data } = await ApiService.put(
            `/access/roles/${this.role.id}`,
            { revision: this.revision, pages: this.draft },
          );
          this.original = clone(data.pages);
          this.draft = clone(data.pages);
          this.revision = data.revision;
          this.savedAt = new Date().toISOString();
          this.reviewDialog = false;
          await refreshAccess();
          this.$toast.success("Role permissions saved.");
        } catch (error) {
          this.error =
            error.response?.data?.message ||
            "Unable to save permissions. Your edits are still here.";
        } finally {
          this.saving = false;
        }
        return;
      }
      try {
        const savedAt = new Date().toISOString();
        localStorage.setItem(
          previewStorageKey(this.role.id),
          JSON.stringify({
            schemaVersion: previewSchemaVersion,
            roleId: this.role.id,
            savedAt,
            pages: this.draft,
          }),
        );
        this.original = clone(this.draft);
        this.savedAt = savedAt;
        this.reviewDialog = false;
        this.$toast.success("Permission preview saved on this browser.");
      } catch (error) {
        this.error =
          "This browser could not save the preview. Your changes are still here. Please try again.";
      }
    },
  },
};
</script>
<style scoped>
.permission-explorer {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  flex: 1;
  color: #30455e;
}
.explorer-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.explorer-context {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 18px 18px 0;
}
.context-role {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.context-label {
  font-size: 10px;
  color: #7d8ca0;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
h3 {
  font-size: 16px;
  margin: 3px 0 0;
  font-weight: 700;
}
.preview-label {
  white-space: nowrap;
  padding: 4px 8px;
  font-size: 10px;
  border: 1px solid #dce6f2;
  color: #637e9c;
  border-radius: 5px;
  background: #f5f8fc;
}
.preview-label .v-icon {
  color: inherit;
  margin-right: 3px;
}
.context-help {
  margin: 6px 18px 14px;
  color: #738398;
  font-size: 11px;
  line-height: 1.6;
}
.explorer-search {
  flex: 0 0 50%;
  min-width: 0;
}
.catalog-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 9px;
  color: #8492a4;
  font-size: 10px;
}
.catalog-meta button {
  color: #1976d2;
}
.menu-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 5px 17px 12px;
  border-bottom: 1px solid #e7edf5;
}
.menu-categories button {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
  border-radius: 7px;
  padding: 8px 10px;
  color: #7b8da2;
  font-size: 11px;
}
.menu-categories button:hover {
  background: #f4f7fb;
}
.menu-categories button .v-icon {
  color: inherit;
  font-size: 15px;
}
.menu-categories .category-selected {
  color: #216aac;
  background: #edf5ff;
  border-color: #d7e8fb;
  font-weight: 600;
}
.menu-picker {
  padding: 14px 17px 15px;
  border-bottom: 1px solid #e7edf5;
  background: #fbfcfe;
}
.picker-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.picker-heading strong {
  color: #637a93;
  font-size: 11px;
}
.picker-heading span {
  color: #96a4b4;
  font-size: 10px;
}
.page-choices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 7px;
  max-height: 172px;
  overflow-y: auto;
  padding: 1px;
}
.page-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 46px;
  padding: 8px 10px;
  border: 1px solid #e5ebf3;
  background: white;
  border-radius: 7px;
  text-align: left;
}
.page-choice:hover {
  background: #f4f8fe;
  border-color: #cadff7;
}
.page-choice > .v-icon {
  color: #8194ab;
  font-size: 16px;
}
.page-choice > span {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.page-choice strong {
  color: #566e88;
  font-size: 11px;
  font-weight: 500;
  overflow-wrap: anywhere;
}
.page-choice small {
  color: #96a6b8;
  font-size: 9px;
}
.page-choice--selected {
  border-color: #aacaee;
  background: #eaf3ff;
}
.page-choice--selected strong,
.page-choice--selected > .v-icon {
  color: #176bb8;
  font-weight: 600;
}
.page-choice .page-enabled-icon {
  color: #74a68d;
  flex: 0 0 auto;
}
.menu-categories button:focus-visible,
.page-choice:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: -2px;
}
.focused-permissions {
  min-width: 0;
  flex: 1;
}
.focused-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 14px 18px;
}
.focused-toolbar > span {
  color: #8899ad;
  font-size: 10px;
  min-width: 0;
}
.focused-toolbar > button {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #6b88a8;
  font-size: 10px;
  flex-shrink: 0;
}
.focused-toolbar > button .v-icon {
  color: inherit;
}
.permission-tree {
  padding: 0 16px 16px;
  min-width: 0;
}
.preview-savebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
  padding: 15px 17px;
  border-top: 1px solid #dfe7f1;
  background: #f8fbff;
  margin-top: auto;
}
.preview-savebar strong {
  font-size: 11px;
  color: #48647f;
}
.preview-savebar p {
  font-size: 10px;
  color: #8997a9;
  margin: 4px 0 0;
  line-height: 1.5;
}
.savebar-actions {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.permission-explorer .v-btn,
.preview-dialog .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
.no-results {
  padding: 45px 15px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}
.no-results p {
  font-size: 12px;
  color: #8996a8;
}
.no-results .v-icon {
  color: #9badbf;
}
.preview-dialog .v-card__title {
  padding: 21px 24px;
}
.preview-dialog .v-card__title p {
  margin: 5px 0 0;
  color: #8593a5;
  font-size: 12px;
}
.review-body {
  padding: 20px 24px !important;
}
.review-notice {
  display: flex;
  gap: 10px;
  padding: 11px 13px;
  margin-bottom: 18px;
  background: #fff9ec;
  border: 1px solid #f2e7d0;
  border-radius: 6px;
}
.review-notice p {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #9a7e45;
}
.review-notice .v-icon {
  color: #9a7e45;
}
.review-page {
  padding: 14px 0;
  border-bottom: 1px solid #e9eef5;
}
.review-page h4 {
  font-size: 13px;
  margin-bottom: 10px;
  color: #405c78;
}
.review-change {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 10px 0;
  color: #76869a;
  font-size: 12px;
}
.review-change span {
  flex: 1;
}
.review-change strong {
  font-size: 11px;
  text-align: right;
}
.change-added {
  color: #418063;
}
.change-removed {
  color: #b66c6c;
}
.review-actions {
  padding: 16px 24px;
}
@media (max-width: 1150px) {
  .preview-savebar {
    flex-wrap: wrap;
  }
  .savebar-actions {
    margin-left: auto;
  }
}
@media (max-width: 550px) {
  .permission-tree {
    max-height: none;
    padding: 4px 11px 14px;
  }
  .explorer-context {
    padding: 15px 14px 0;
    flex-wrap: wrap;
  }
  .context-help {
    margin-left: 14px;
    margin-right: 14px;
  }
  .explorer-search {
    flex-basis: 100%;
    padding-top: 8px;
  }
  .preview-savebar {
    padding: 13px;
  }
  .menu-categories {
    padding: 5px 12px 11px;
    gap: 4px;
  }
  .menu-categories button {
    padding: 7px 8px;
    font-size: 10px;
  }
  .menu-picker {
    padding: 12px;
  }
  .page-choices {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: 164px;
  }
  .focused-toolbar {
    padding: 12px;
    flex-wrap: wrap;
  }
}
</style>
